import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { debounceTime, filter, startWith, takeUntil } from 'rxjs/operators';
import { CustomerService } from '../../shared/';

import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField, MatHint } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import { GridOptions } from 'ag-grid-community';
import { Subject } from 'rxjs';
import { customerNameComparatorFactory } from 'src/app/shared/helpers/CustomerNameComparatorFactory';
import { Customer } from '../../models';
import { CustomerNameCellComponent } from '../../shared/ag-grid-cells/customer-name-cell.component';
import { NewCustomerDialogComponent } from '../../shared/dialogs/new-customer-dialog/new-customer-dialog.component';
import { CustomerAddressPipe } from '../../shared/pipes/customer-address.pipe';
import { CustomerContactDetailsPipe } from '../../shared/pipes/customer-contact-details.pipe';
import { CustomerNamePipe } from '../../shared/pipes/customer-name.pipe';

@Component({
  selector: 'list-customers',
  templateUrl: './list.customers.html',
  styleUrls: ['list.customers.component.scss'],
  providers: [CustomerService, CustomerContactDetailsPipe, CustomerAddressPipe],
  standalone: true,
  imports: [
    MatFormField,
    MatInput,
    ReactiveFormsModule,
    AgGridModule,
    MatHint,
    MatButton
  ]
})
export class ListCustomersComponent implements OnInit {
  @ViewChild('agGrid') grid: AgGridAngular;

  public customers: Customer[];
  public filteredCustomers: Customer[];
  filter = new UntypedFormControl();
  height = 600;

  gridOptions: GridOptions = {
    headerHeight: 45,
    onGridReady: this.gridReady.bind(this)
  };

  public columnDefinitions: Array<any> = [
    {
      headerName: 'Customer',
      cellRenderer: CustomerNameCellComponent,
      width: 240,
      sortable: true,
      comparator: customerNameComparatorFactory(this.customerNamePipe),
      resizable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        valueGetter: (value) => {
          return value.full_name;
        },
        caseSensitive: false,
        clearButton: true
      },
      valueGetter: (params) => {
        return params.data;
      }
    },
    {
      headerName: 'Address',
      valueGetter: (params) => {
        return this.customerAddressPipe.transform(params.data);
      }
    },
    {
      headerName: 'Contact',
      valueGetter: (params) => {
        return this.customerContactDetailsPipe.transform(params.data);
      }
    }
  ];
  destroy$ = new Subject<void>();

  public constructor(
    private customerService: CustomerService,
    private router: Router,
    private customerNamePipe: CustomerNamePipe,
    private customerContactDetailsPipe: CustomerContactDetailsPipe,
    private customerAddressPipe: CustomerAddressPipe,
    private dialog: MatDialog
  ) {}

  public ngOnInit() {
    this.customerService.getCustomers().subscribe((response) => {
      this.customers = response;
      if (response.length) {
        this.grid.api.hideOverlay();
      } else if (!response.length) {
        this.grid.api.showNoRowsOverlay();
      }
      this.filterCustomers(this.filter.value ?? '');
    });
  }

  gridReady() {
    this.grid.api.showLoadingOverlay();
    if (this.customers) {
      this.grid.api.setRowData(this.customers);
      this.grid.api.hideOverlay();
    }
    this.filter.valueChanges
      .pipe(
        debounceTime(200),
        startWith(this.filter.value ?? ''),
        filter(() => Boolean(this.customers)),
        takeUntil(this.destroy$)
      )
      .subscribe((search) => {
        if (!search) {
          this.grid.api.setGridOption('rowData', this.customers);
          return;
        }
        const filteredCustomers = this.customers.filter(
          (customer) =>
            customer.full_name.toLowerCase().includes(search.toLowerCase()) ||
            this.customerAddressPipe
              .transform(customer)
              .toLowerCase()
              .includes(search.toLowerCase()) ||
            this.customerContactDetailsPipe
              .transform(customer)
              .toLowerCase()
              .includes(search.toLowerCase()) ||
            (customer.account_number ?? '')
              .toLowerCase()
              .includes(search.toLowerCase())
        );
        this.grid.api.setGridOption('rowData', filteredCustomers);
      });
  }

  filterCustomers(search: string) {
    const filteredCustomers = this.customers.filter(
      (customer) =>
        customer.full_name.toLowerCase().includes(search.toLowerCase()) ||
        this.customerAddressPipe
          .transform(customer)
          .toLowerCase()
          .includes(search.toLowerCase()) ||
        this.customerContactDetailsPipe
          .transform(customer)
          .toLowerCase()
          .includes(search.toLowerCase()) ||
        (customer.account_number ?? '')
          .toLowerCase()
          .includes(search.toLowerCase())
    );
    this.grid.api.setGridOption('rowData', filteredCustomers);
  }

  addCustomer() {
    this.dialog
      .open(NewCustomerDialogComponent, {
        maxHeight: '90vh',
        minWidth: '50vw'
      })
      .afterClosed()
      .pipe(filter((customer) => !!customer))
      .subscribe((customer) => {
        this.router.navigate(['/customer', customer.id]);
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
